import type { UserGamePack } from "@skillscratch/shared-types";
import { useFirebaseUser } from "./useFirebaseUser";
import { collection, getDocs, onSnapshot, query, where } from "firebase/firestore";
import { firestore } from "@/helpers/firebase";
import { useState, useEffect } from "react";


export function useGamePacks(): [UserGamePack[] | undefined, boolean, Error | undefined] {
  const user = useFirebaseUser();
  const [gamePacks, setGamePacks] = useState<UserGamePack[] | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | undefined>(undefined);
  
  useEffect(() => {
    const userId = user?.uid;
    if (!userId) {
      setGamePacks(undefined);
      setLoading(false);
      return;
    }
    
    // Reference to the user's game packs collection
    const userGamePackRef = collection(firestore, "user", userId, "gamePack");
    
    // Set up onSnapshot listener
    const unsubscribe = onSnapshot(
      userGamePackRef,
      async (snapshot) => {
        try {
          const docs = [...snapshot.docs];
          
          // Process each pack with session counts
          const processedPacks = await Promise.all(
            docs.map(async (doc) => {
              const pack = doc.data() as UserGamePack;
              
              // Get total session count
              const totalSessionSnapshot = await getDocs(
                query(collection(doc.ref, "session"))
              );
              const totalSessionCount = totalSessionSnapshot.size;
              
              // Get count of incomplete sessions
              const incompleteSessionSnapshot = await getDocs(
                query(collection(doc.ref, "session"), where("completed", "==", false))
              );
              const incompleteSessionCount = incompleteSessionSnapshot.size;
              
              // Calculate used sessions (total - incomplete)
              const usedSessionCount = totalSessionCount - incompleteSessionCount;
              
              // Available count is the pack's total count minus used sessions
              const count = pack.count - usedSessionCount;
              
              return {
                id: pack.id,
                gameThemeId: pack.gameThemeId,
                count,
                storeId: pack.storeId,
                usedCount: usedSessionCount,
              } satisfies UserGamePack;
            })
          );
          
          // Filter out packs with zero count
          const availablePacks = processedPacks.filter((pack) => pack.count > 0);
          
          setGamePacks(availablePacks);
          setLoading(false);
          setError(undefined);
        } catch (err) {
          setGamePacks(undefined);
          setLoading(false);
          setError(err instanceof Error ? err : new Error(String(err)));
        }
      },
      (err) => {
        setGamePacks(undefined);
        setLoading(false);
        setError(err);
      }
    );
    
    // Clean up listener on unmount
    return () => unsubscribe();
  }, [user]);
  
  return [gamePacks, loading, error];
}